import React, { useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import InnerLayout from "../../../components/layouts/inner-layout";
import AddButton from "../../../components/buttons/button";
import { customReportListAction, customReportDeleteAction } from "../../../redux/actions/custom-report-action";
import { PlusIcon } from "@heroicons/react/24/outline";
import { FileText, Pencil, Trash2, Star, Users, Lock } from "lucide-react";
import LoadingButtonSpinner from "../../../components/helper-components/loading-button-spinner";

const bgClassMap = {
  "reports-shade1_bg": "bg-reports-shade1_bg",
  "reports-shade2_bg": "bg-reports-shade2_bg",
  "reports-shade3_bg": "bg-reports-shade3_bg",
  "reports-shade4_bg": "bg-reports-shade4_bg",
  "reports-shade5_bg": "bg-reports-shade5_bg",
  "reports-shade6_bg": "bg-reports-shade6_bg",
};

const textClassMap = {
  "reports-shade1_text": "text-reports-shade1_text",
  "reports-shade2_text": "text-reports-shade2_text",
  "reports-shade3_text": "text-reports-shade3_text",
  "reports-shade4_text": "text-reports-shade4_text",
  "reports-shade5_text": "text-reports-shade5_text",
  "reports-shade6_text": "text-reports-shade6_text",
};

// Color combos for cards
const combos = {
  1: ["reports-shade1_text", "reports-shade1_bg"],
  2: ["reports-shade2_text", "reports-shade2_bg"],
  3: ["reports-shade3_text", "reports-shade3_bg"],
  4: ["reports-shade4_text", "reports-shade4_bg"],
  5: ["reports-shade5_text", "reports-shade5_bg"],
  6: ["reports-shade6_text", "reports-shade6_bg"],
};

const CustomReportsMain = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [reports, setReports] = useState([]);
  const [loading, setLoading] = useState(true);
  const [deleteModalVisible, setDeleteModalVisible] = useState(false);
  const [reportToDelete, setReportToDelete] = useState(null);

  useEffect(() => {
    fetchReports();
  }, []);

  const fetchReports = () => {
    setLoading(true);
    dispatch(
      customReportListAction(
        (response) => {
          setReports(response.data || []);
          setLoading(false);
        },
        (error) => {
          console.error("Failed to fetch reports:", error);
          setLoading(false);
        }
      )
    );
  };

  const handleCardClick = (reportId) => {
    navigate(`/reports/custom-reports/view/${reportId}`);
  };

  const handleEditClick = (e, reportId) => {
    e.stopPropagation();
    navigate(`/reports/custom-reports/editor/${reportId}`);
  };

  const handleDeleteClick = (e, report) => {
    e.stopPropagation();
    setReportToDelete(report);
    setDeleteModalVisible(true);
  };

  const handleConfirmDelete = () => {
    if (!reportToDelete) return;
    dispatch(
      customReportDeleteAction(
        { report_id: reportToDelete.report_id },
        () => {
          fetchReports();
          setDeleteModalVisible(false);
          setReportToDelete(null);
        },
        (error) => {
          console.error("Failed to delete report:", error);
          setDeleteModalVisible(false);
          setReportToDelete(null);
        }
      )
    );
  };

  const handleCancelDelete = () => {
    setDeleteModalVisible(false);
    setReportToDelete(null);
  };

  const formatTimeAgo = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return "just now";
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 7) return `${diffDays}d ago`;
    return date.toLocaleDateString("en-US", { month: "short", day: "numeric" });
  };

  // Assign a consistent color combo based on report index
  const getCombo = (index) => {
    return ((index % 6) + 1);
  };

  return (
    <InnerLayout title="Custom Reports" backBtn={true} list={true}>
      <div className="px-6 py-4">
        <div className="flex justify-between items-center mb-6">
          <div>
            <h2 className="text-lg fw_600 text-344054">Custom Reports</h2>
            <p className="text-sm text-667085">Create and manage custom SQL-based reports</p>
          </div>
          <AddButton
            text="Create Report"
            icon={<PlusIcon className="w-4 h-4" />}
            onclkFn={() => navigate("/reports/custom-reports/editor")}
          />
        </div>

        {loading ? (
          <LoadingButtonSpinner />
        ) : reports.length === 0 ? (
          <div className="flex flex-col items-center justify-center h-64 text-667085">
            <FileText size={48} className="mb-4 text-D0D5DD" />
            <p className="text-sm mb-2">No custom reports yet</p>
            <p className="text-xs">Click "Create Report" to create your first custom report</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 xl:grid-cols-4 gap-6">
            {reports.map((report, idx) => {
              const combo = getCombo(idx);
              return (
                <div
                  key={report.report_id}
                  className="bg-white rounded-xl p-4 shadow-md border border-gray-200 flex flex-col justify-between
                            transform transition-transform duration-200 hover:-translate-y-1 cursor-pointer"
                  onClick={() => handleCardClick(report.report_id)}
                >
                  {/* Header with icon and actions */}
                  <div className="flex justify-between items-start">
                    <div className={`rounded-md px-2 py-2 ${bgClassMap[combos[combo][1]]}`}>
                      <FileText
                        className={textClassMap[combos[combo][0]]}
                        size={20}
                      />
                    </div>
                    <div className="flex gap-2">
                      <Pencil
                        className="text-787878 cursor-pointer hover:text-primary"
                        size={16}
                        onClick={(e) => handleEditClick(e, report.report_id)}
                      />
                      <Trash2
                        className="text-787878 hover:text-F94B4B cursor-pointer"
                        size={16}
                        onClick={(e) => handleDeleteClick(e, report)}
                      />
                    </div>
                  </div>

                  {/* Title and description */}
                  <div className="mt-4">
                    <h3 className="text-sml font-semibold mb-1 truncate">{report.name}</h3>
                    <p className="text-xs text-787878 mb-1">Custom Generated</p>
                    <p className="text-sm text-787878 line-clamp-2">
                      {report.description || "No description"}
                    </p>
                  </div>

                  {/* Tags */}
                  <div className="flex justify-between items-center mt-4 mb-1">
                    <div className="flex gap-2 flex-wrap">
                      <span
                        className={`text-xs font-medium px-2.5 py-0.5 rounded ${bgClassMap[combos[combo][1]]} ${textClassMap[combos[combo][0]]}`}
                      >
                        Custom
                      </span>
                      {report.is_shared && (
                        <span className="text-xs font-medium px-2.5 py-0.5 rounded bg-reports-shade4_bg text-reports-shade4_text flex items-center gap-1">
                          <Users size={12} />
                          Shared
                        </span>
                      )}
                      {!report.is_shared && (
                        <span className="text-xs font-medium px-2.5 py-0.5 rounded bg-reports-shade6_bg text-reports-shade6_text flex items-center gap-1">
                          <Lock size={12} />
                          Private
                        </span>
                      )}
                    </div>
                  </div>

                  {/* Updated timestamp */}
                  <span className="text-xs text-787878">
                    Updated {formatTimeAgo(report.update_date || report.created_date)}
                  </span>
                </div>
              );
            })}
          </div>
        )}

        {/* Delete confirmation modal */}
        {deleteModalVisible && (
          <div
            className="modal-background fixed inset-0 bg-gradient-to-br from-black/60 via-black/50 to-black/40 backdrop-blur-sm flex items-center justify-center z-50 p-4"
            onClick={handleCancelDelete}
          >
            <div
              className="bg-white p-6 rounded-lg w-80 border border-gray-200 shadow-xl"
              onClick={(e) => e.stopPropagation()}
            >
              <h2 className="text-lg font-medium mb-4">Delete Report</h2>
              <p className="text-sm mb-4 text-667085">
                Are you sure you want to delete "{reportToDelete?.name}"? This action cannot be undone.
              </p>
              <div className="flex justify-end space-x-4">
                <button
                  className="px-4 py-2 bg-white border border-D0D5DD text-344054 rounded hover:bg-gray-50"
                  onClick={handleCancelDelete}
                >
                  Cancel
                </button>
                <button
                  className="px-4 py-2 bg-F94B4B text-white rounded hover:bg-red-600"
                  onClick={handleConfirmDelete}
                >
                  Delete
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </InnerLayout>
  );
};

export default CustomReportsMain;
